import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ value = 0, size = 14 }) {
  return (
    <span className="star-row" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          fill={n <= value ? "currentColor" : "none"}
          strokeWidth={1.5}
        />
      ))}
    </span>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ avg: null, count: 0 });

  useEffect(() => {
    axios
      .get(`${API}/reviews?limit=9`)
      .then((r) => {
        setReviews(r.data.reviews || []);
        setSummary(r.data.summary || { avg: null, count: 0 });
      })
      .catch(() => {});
  }, []);

  return (
    <section className="reviews-section section-pad" data-testid="reviews-section">
      <div className="reviews-head">
        <div>
          <p className="eyebrow">Guest voices</p>
          <h2>What visitors say</h2>
          {summary.count > 0 ? (
            <p className="muted">
              <Stars value={Math.round(summary.avg || 0)} /> &nbsp;
              <strong>{summary.avg}</strong> average from <strong>{summary.count}</strong> verified visit{summary.count === 1 ? "" : "s"}.
            </p>
          ) : (
            <p className="muted">We publish every honest review our visitors send — once our team has verified the visit.</p>
          )}
        </div>
        <Link to="/share-your-experience/" className="btn btn-secondary" data-testid="share-experience-cta">
          Share your experience
        </Link>
      </div>

      {reviews.length === 0 ? (
        <div className="reviews-empty muted-small">
          Be the first to share what your Zorba visit felt like.
        </div>
      ) : (
        <div className="reviews-grid">
          {reviews.map((r, i) => (
            <article key={r.id} className="review-card" data-testid={`review-card-${i}`}>
              <Quote size={18} className="review-quote-icon" />
              <Stars value={r.rating} />
              <p className="review-text">{r.text}</p>
              {r.photos?.length > 0 && (
                <div className="review-photos">
                  {r.photos.slice(0, 3).map((p, idx) => (
                    <img
                      key={p}
                      src={`${API}/files/${p}`}
                      alt={`${r.name} shared ${idx + 1}`}
                      loading="lazy"
                    />
                  ))}
                </div>
              )}
              <footer className="review-meta">
                <strong>{r.name}</strong>
                <span className="muted-small">
                  {r.service} · {formatDate(r.created_at)}
                </span>
              </footer>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
